import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCheck } from 'lucide-react';
import { NotificationList } from '@/components/notifications/NotificationList';
import { useAppNotifications } from '@/contexts/NotificationsContext';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

type FilterTab = 'all' | 'unread';

export default function Notifications() {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterTab>('all');
  const { notifications, loading, unreadCount, markAsRead, markAllAsRead } = useAppNotifications();

  const visibleNotifications = useMemo(() => {
    if (filter === 'unread') {
      return notifications.filter((item) => item.readAt === null);
    }
    return notifications;
  }, [filter, notifications]);

  const handleItemClick = (id: string, contactId?: string | null) => {
    void markAsRead(id);
    if (contactId) {
      navigate(`/crm/${contactId}`);
    }
  };

  return (
    <div className="flex w-full flex-col gap-6 text-[#0F172A] dark:text-[#FAFAFA]">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight xl:text-3xl">{t('title')}</h1>
          <p className="mt-1 text-sm text-[#64748B] dark:text-[#A1A1AA]">
            {t('subtitle')}
          </p>
        </div>

        <button
          type="button"
          onClick={() => void markAllAsRead()}
          disabled={unreadCount === 0}
          className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-[#2563EB] transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40 dark:text-[#60A5FA]"
        >
          <CheckCheck className="h-4 w-4" />
          {t('markAllRead')}
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-sm dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none">
        <div className="flex gap-2 border-b border-[#E5E7EB] px-4 py-3 dark:border-[#3F3F464D]">
          <button
            type="button"
            onClick={() => setFilter('all')}
            className={cn(
              'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
              filter === 'all'
                ? 'bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]'
                : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0] dark:bg-[#3F3F46] dark:text-[#A1A1AA] dark:hover:bg-[#52525B]'
            )}
          >
            {t('tabAll')}
          </button>
          <button
            type="button"
            onClick={() => setFilter('unread')}
            className={cn(
              'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
              filter === 'unread'
                ? 'bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]'
                : 'bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0] dark:bg-[#3F3F46] dark:text-[#A1A1AA] dark:hover:bg-[#52525B]'
            )}
          >
            {unreadCount > 0 ? t('tabUnreadCount', { count: unreadCount }) : t('tabUnread')}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#E5E7EB] border-t-[#2563EB] dark:border-[#3F3F46] dark:border-t-[#60A5FA]" />
          </div>
        ) : (
          <NotificationList
            notifications={visibleNotifications}
            onItemClick={handleItemClick}
            emptyMessage={
              filter === 'unread' ? t('emptyUnread') : t('empty')
            }
          />
        )}
      </div>
    </div>
  );
}
